// modules
import React, { FC, useCallback, useMemo } from 'react';
import { Button, Text, View } from '@upstox/upstox-ui';
import { useStore } from 'modules/Stores';
import { observer } from 'mobx-react';
import LocaleService from 'modules/Localization';
import PriceAlerts from 'modules/PriceAlerts';
import LiveData from 'modules/LiveData';
// styles
import * as css from './styles';

const CreatePriceAlertsFooter: FC = () => {
  const {
    currentAlertId,
    priceAboveValue,
    priceBelowValue,
    createAlert,
    getPriceAlerts,
    editAlert,
    setModalType,
    clearAlertForm,
    priceAboveError,
    priceBelowError,
    scripData,
  } = useStore('PriceAlertsStore');

  const { getLoading } = useStore('LoadingStore');

  const isCreatingAlert = getLoading('CREATE_PRICE_ALERT');

  const isEditingAlert = getLoading('EDIT_PRICE_ALERT');

  const BUTTON_TEXT = useMemo(
    () =>
      (currentAlertId
        ? LocaleService.translate('UPDATE_BUTTON')
        : LocaleService.translate('CREATE_BUTTON')),
    [currentAlertId],
  );

  const disabled =
    (!priceAboveValue && !priceBelowValue) ||
    !!priceAboveError ||
    !!priceBelowError;

  const loading = isCreatingAlert || isEditingAlert;

  const handleCancelClick = useCallback(() => {
    clearAlertForm();
    setModalType('List');
  }, [setModalType, clearAlertForm]);

  const handleAction = useCallback(async () => {
    let action;
    let exchangeToken;
    let instrumentKey;
    if (currentAlertId) {
      const alertToEdit = PriceAlerts
        .priceAlertList.find(alert => alert.id === currentAlertId);
      exchangeToken = `${alertToEdit?.instrument.e}|${alertToEdit?.instrument.t}`;
      action = editAlert;
      instrumentKey = alertToEdit?.instrumentKey;
    } else {
      action = createAlert;
      exchangeToken = scripData?.exchangeToken;
      instrumentKey = scripData?.instrumentKey;
    }
    const liveData = LiveData.getByInstrumentKey(instrumentKey || '');
    const ltp = liveData?.lastTradedPrice;
    const response = await action(exchangeToken, Number(ltp));

    if (response?.success) {
      setModalType('List');
      getPriceAlerts();
      clearAlertForm();
    }
  }, [
    currentAlertId,
    createAlert,
    editAlert,
    clearAlertForm,
    setModalType,
    getPriceAlerts,
    scripData,
  ]);

  return (
    <View cssProps={{ loading }} css={css.container}>
      <Text css={css.text}>
        {LocaleService.translate('CREATE_PRICE_ALERTS_NOTE')}
      </Text>
      <View gap={12}>
        <Button css={css.button} onClick={handleCancelClick}>
          {LocaleService.translate('CANCEL_BUTTON')}
        </Button>
        <Button
          cssProps={{ disabled }}
          css={css.createButton}
          isLoading={loading}
          onClick={handleAction}
          disabled={disabled}
        >
          {BUTTON_TEXT}
        </Button>
      </View>
    </View>
  );
};

export default observer(CreatePriceAlertsFooter);
